import React from "react";
import { KgInspectorDataSourceOverview } from "../../../molecules/KgInspectorDataSourceOverview";
import { KgInspectorLinksTab } from "../../../molecules/KgInspectorLinksTab";
import { KgInspectorMetricsTab } from "../../../molecules/KgInspectorMetricsTab";
import { KgInspectorOntologyOverview } from "../../../molecules/KgInspectorOntologyOverview";
import { KgInspectorRelationshipTab } from "../../../molecules/KgInspectorRelationshipTab";
import { KgInspectorReportsTab } from "../../../molecules/KgInspectorReportsTab";
import { KgInspectorRulesTab } from "../../../molecules/KgInspectorRulesTab";
import { KgInspectorTableOverview } from "../../../molecules/KgInspectorTableOverview";
import { KgInspectorTablesTab } from "../../../molecules/KgInspectorTablesTab";
import { KgInspectorMetricFormulaTab } from "../../../molecules/KgInspectorMetricFormulaTab";
import { KgInspectorMetricOverview } from "../../../molecules/KgInspectorMetricOverview";
import { KgInspectorDataStoryOverview } from "../../../molecules/KgInspectorDataStoryOverview";
import { KgInspectorArtifactOverview } from "../../../molecules/KgInspectorArtifactOverview";
import { KgInspectorArtifactActivityTab } from "../../../molecules/KgInspectorArtifactActivityTab";
import { KgInspectorReportActivityTab } from "../../../molecules/KgInspectorReportActivityTab";
import { KgInspectorReportNativeOverview } from "../../../molecules/KgInspectorReportNativeOverview";

import { KgInspectorUsedInTab } from "../../../molecules/KgInspectorUsedInTab";
import type { KgFlowNode, KgFlowNodeType } from "../types";
import {
  isReportNativeOverview,
  isReportOverviewEnabled,
} from "./reportInspectorConfig";
import { ARTIFACTS_SECTION_ID } from "./dataStoryInspectorConfig";
import type { InspectorTabId, KgInspectorTabData } from "./types";
import KgInspectorReportOverview from "../../../molecules/KgInspectorReportOverview";

export interface InspectorTabRenderContext {
  node: KgFlowNode;
  tabData: KgInspectorTabData;
  expandedSections: Record<string, boolean>;
  onToggleSection: (sectionId: string) => void;
}

type TabContentRenderer = (ctx: InspectorTabRenderContext) => React.ReactNode;

export const INSTANCES_SECTION_ID = "instances";
export const COLUMNS_SECTION_ID = "columns";
export const DATA_SOURCES_SECTION_ID = "dataSources";
export const FORMULA_SECTION_ID = "formula";
export const ASSOCIATED_COLUMNS_SECTION_ID = "associatedColumns";

const dataSourceTabRenderers: Partial<
  Record<InspectorTabId, TabContentRenderer>
> = {
  overview: (ctx) => (
    <KgInspectorDataSourceOverview
      overview={ctx.tabData.overview}
      isInstancesOpen={ctx.expandedSections[INSTANCES_SECTION_ID] ?? false}
      onToggleInstances={() => ctx.onToggleSection(INSTANCES_SECTION_ID)}
    />
  ),
  tables: (ctx) => <KgInspectorTablesTab tables={ctx.tabData.tables ?? []} />,
  usedIn: (ctx) => (
    <KgInspectorUsedInTab
      sections={ctx.tabData.usedInSections ?? []}
      expandedSectionIds={ctx.expandedSections}
      onToggleSection={ctx.onToggleSection}
    />
  ),
  reports: (ctx) => (
    <KgInspectorReportsTab
      reports={(ctx.tabData.reports ?? []).map((row) => ({
        title: row.title,
        secondary: row.connectedOn,
        isLinked: row.isLinked,
        canvasNodeId: row.canvasNodeId,
      }))}
    />
  ),
};

const tableTabRenderers: Partial<Record<InspectorTabId, TabContentRenderer>> = {
  overview: (ctx) => (
    <KgInspectorTableOverview
      overview={ctx.tabData.tableOverview}
      isColumnsOpen={ctx.expandedSections[COLUMNS_SECTION_ID] ?? false}
      onToggleColumns={() => ctx.onToggleSection(COLUMNS_SECTION_ID)}
    />
  ),
  relationship: (ctx) => (
    <KgInspectorRelationshipTab
      relationships={ctx.tabData.relationships ?? []}
    />
  ),
  metrics: (ctx) => (
    <KgInspectorMetricsTab metrics={ctx.tabData.metrics ?? []} />
  ),
  usedIn: (ctx) => (
    <KgInspectorUsedInTab
      sections={ctx.tabData.usedInSections ?? []}
      expandedSectionIds={ctx.expandedSections}
      onToggleSection={ctx.onToggleSection}
    />
  ),
};

const metricTabRenderers: Partial<Record<InspectorTabId, TabContentRenderer>> = {
  overview: (ctx) => (
    <KgInspectorMetricOverview overview={ctx.tabData.metricOverview} />
  ),
  formula: (ctx) => (
    <KgInspectorMetricFormulaTab
      formula={ctx.tabData.metricFormula}
      isFormulaOpen={ctx.expandedSections[FORMULA_SECTION_ID] ?? false}
      isColumnsOpen={
        ctx.expandedSections[ASSOCIATED_COLUMNS_SECTION_ID] ?? false
      }
      onToggleFormula={() => ctx.onToggleSection(FORMULA_SECTION_ID)}
      onToggleColumns={() =>
        ctx.onToggleSection(ASSOCIATED_COLUMNS_SECTION_ID)
      }
    />
  ),
  usedIn: (ctx) => (
    <KgInspectorUsedInTab
      sections={ctx.tabData.usedInSections ?? []}
      expandedSectionIds={ctx.expandedSections}
      onToggleSection={ctx.onToggleSection}
      emptyHint="Nothing downstream uses this metric yet."
    />
  ),
};

const dataStoryTabRenderers: Partial<
  Record<InspectorTabId, TabContentRenderer>
> = {
  overview: (ctx) => (
    <KgInspectorDataStoryOverview
      overview={ctx.tabData.dataStoryOverview}
      isArtifactsOpen={ctx.expandedSections[ARTIFACTS_SECTION_ID] ?? true}
      onToggleArtifacts={() => ctx.onToggleSection(ARTIFACTS_SECTION_ID)}
    />
  ),
  trace: (ctx) => (
    <KgInspectorUsedInTab
      sections={ctx.tabData.traceSections ?? []}
      expandedSectionIds={ctx.expandedSections}
      onToggleSection={ctx.onToggleSection}
      emptyHint="No upstream lineage for this data story yet."
    />
  ),
  activity: (ctx) => (
    <KgInspectorReportActivityTab
      rows={ctx.tabData.dataStoryActivity ?? []}
      variant="dataStory"
    />
  ),
};

const reportTabRenderers: Partial<Record<InspectorTabId, TabContentRenderer>> = {
  overview: (ctx) => {
    const overview = ctx.tabData.reportOverview;
    if (!overview || !isReportOverviewEnabled(overview.sourceType)) {
      return null;
    }
    if (isReportNativeOverview(overview.sourceType)) {
      return <KgInspectorReportNativeOverview overview={overview} />;
    }
    return <KgInspectorReportOverview overview={overview} />;
  },
  trace: (ctx) => (
    <KgInspectorUsedInTab
      sections={ctx.tabData.traceSections ?? []}
      expandedSectionIds={ctx.expandedSections}
      onToggleSection={ctx.onToggleSection}
      emptyHint="No upstream lineage for this report yet."
    />
  ),
  usedIn: (ctx) => (
    <KgInspectorUsedInTab
      sections={ctx.tabData.usedInSections ?? []}
      expandedSectionIds={ctx.expandedSections}
      onToggleSection={ctx.onToggleSection}
      emptyHint="Nothing downstream uses this report yet."
    />
  ),
  activity: (ctx) => (
    <KgInspectorReportActivityTab rows={ctx.tabData.reportActivity ?? []} />
  ),
};

const artifactTabRenderers: Partial<
  Record<InspectorTabId, TabContentRenderer>
> = {
  overview: (ctx) => (
    <KgInspectorArtifactOverview overview={ctx.tabData.artifactOverview} />
  ),
  activity: (ctx) => (
    <KgInspectorArtifactActivityTab activity={ctx.tabData.artifactActivity} />
  ),
};

const ontologyTabRenderers: Partial<
  Record<InspectorTabId, TabContentRenderer>
> = {
  overview: (ctx) => (
    <KgInspectorOntologyOverview
      overview={ctx.tabData.ontologyOverview}
      isDataSourcesOpen={
        ctx.expandedSections[DATA_SOURCES_SECTION_ID] ?? true
      }
      onToggleDataSources={() =>
        ctx.onToggleSection(DATA_SOURCES_SECTION_ID)
      }
    />
  ),
  links: (ctx) => <KgInspectorLinksTab links={ctx.tabData.links ?? []} />,
  metrics: (ctx) => (
    <KgInspectorMetricsTab
      metrics={ctx.tabData.metrics ?? []}
      emptyHint="No metrics linked to this ontology yet."
    />
  ),
  rules: (ctx) => (
    <KgInspectorRulesTab
      sections={ctx.tabData.ruleSections ?? []}
      emptyHint="No rules linked to this ontology yet."
    />
  ),
  reports: (ctx) => (
    <KgInspectorReportsTab
      reports={(ctx.tabData.ontologyReports ?? []).map((row) => ({
        title: row.title,
        secondary: row.dataSource,
        isLinked: row.isLinked,
        canvasNodeId: row.canvasNodeId,
      }))}
      columnLabels={{ primary: "REPORT NAME", secondary: "DATA SOURCE" }}
      emptyHint="No reports linked to this ontology yet."
    />
  ),
  usedIn: (ctx) => (
    <KgInspectorUsedInTab
      sections={ctx.tabData.usedInSections ?? []}
      expandedSectionIds={ctx.expandedSections}
      onToggleSection={ctx.onToggleSection}
      emptyHint="Nothing downstream uses this ontology yet."
    />
  ),
};

/** Data model panel — all four tabs implemented (Overview, Tables, Metrics, Relationships). */
const dataModelTabRenderers: Partial<
  Record<InspectorTabId, TabContentRenderer>
> = {
  overview: (ctx) => (
    <KgInspectorOntologyOverview
      overview={ctx.tabData.dataModelOverview}
      showDataSources={false}
      descriptionEmptyHint="No description available for this data model yet."
    />
  ),
  tables: (ctx) => (
    <KgInspectorTablesTab
      tables={ctx.tabData.tables ?? []}
      emptyHint="No tables linked to this data model yet."
    />
  ),
  metrics: (ctx) => (
    <KgInspectorMetricsTab
      metrics={ctx.tabData.metrics ?? []}
      emptyHint="No metrics linked to this data model yet."
    />
  ),
  relationships: (ctx) => (
    <KgInspectorRelationshipTab
      pairs={ctx.tabData.relationshipPairs ?? []}
      emptyHint="No relationships defined for this data model yet."
    />
  ),
};

const tabContentByNodeType: Partial<
  Record<KgFlowNodeType, Partial<Record<InspectorTabId, TabContentRenderer>>>
> = {
  dataSource: dataSourceTabRenderers,
  table: tableTabRenderers,
  ontology: ontologyTabRenderers,
  metric: metricTabRenderers,
  report: reportTabRenderers,
  dataStory: dataStoryTabRenderers,
  artifact: artifactTabRenderers,
  dataModel: dataModelTabRenderers,
};

export const renderInspectorTabContent = (
  nodeType: KgFlowNodeType,
  tabId: string,
  ctx: InspectorTabRenderContext,
  emptyFallback: React.ReactNode,
  loadingFallback: React.ReactNode,
  errorFallback: (message: string) => React.ReactNode,
): React.ReactNode => {
  if (ctx.tabData.isLoading) return loadingFallback;
  if (ctx.tabData.error) return errorFallback(ctx.tabData.error);

  const renderer =
    tabContentByNodeType[nodeType]?.[tabId as InspectorTabId] ?? null;
  if (!renderer) return emptyFallback;
  const content = renderer(ctx);
  return content ?? emptyFallback;
};
